import React, { useMemo, useState } from 'react';
import moment, { Moment } from 'moment-timezone';
import {
	CreateCaseTarget,
	useLazyWorkplaceCreateCaseTargetQuery,
} from '../../../../graphql/graphql';
import Loading from '../../../../Components/Loading';
import { getMomentDate } from '../../../../Utils/dates.util';
import apiClient from '../../../../Services/ApiClient';
import useNotification from '../../../../Hooks/useNotification.hook';
import useApiClient from '../../../Base/Hooks/useApiClient';
import useRestApiClient from '../../../Base/Hooks/useRestApiClient';
import {
	CalendarAbsenceEventDefaultValues,
	CalendarAbsenceEventFormValues,
} from './CalendarAbsenceEventForm';
import { EventClickArg } from '@fullcalendar/react';
import { useEffectAsync } from '../../../../helpers';
import { getDateWithTime } from '../../../Base/Helpers/date.helper';
import CalendarWorkplaceAbsenceEvent from './CalendarWorkplaceAbsenceEvent';
import CalendarWorkplaceMemberAbsenceEvent from './CalendarWorkplaceMemberAbsenceEvent';

interface Props {
	selectedDate: Moment;
	workplaceId: string;
	onSuccess?: () => void;
	event?: EventClickArg;
}

const CalendarAbsenceEvent: React.FC<Props> = ({
	selectedDate,
	workplaceId,
	onSuccess,
	event,
}) => {
	const { showNotificationSuccess } = useNotification();
	const [isLoading, setIsLoading] = useState(false);
	const [createCaseTarget, setCreateCaseTarget] = useState<
		CreateCaseTarget | undefined
	>();
	const [getWorkplaceCreateCaseTarget] =
		useLazyWorkplaceCreateCaseTargetQuery();
	const { displayError, displayGQLError } = useApiClient();
	const { showUnexpectedError } = useRestApiClient();
	const eventId: string | undefined = event?.event.id;

	useEffectAsync(async () => {
		setIsLoading(true);
		//LOADING WORKPLACE CREATE CASE TARGET SETTINGS
		const data = await getWorkplaceCreateCaseTarget({ id: workplaceId });
		setCreateCaseTarget(data?.data?.workplace?.createCaseTarget || undefined);
		setIsLoading(false);
	}, []);

	const defaultValues: CalendarAbsenceEventDefaultValues = useMemo(() => {
		if (event) {
			const eventData = event.event;
			const start = moment(eventData.start);
			const end = moment(eventData.end);
			return {
				dateFrom: start,
				dateTo: end,
				timeFrom: start,
				timeTo: end,
				workplaceMember: {
					value: eventData?.extendedProps?.doctor?.id,
					label: eventData?.extendedProps?.doctor?.name,
				},
				reason: eventData?.extendedProps?.note,
				allowContactInAbsence: eventData?.extendedProps.meta.find(
					(item: { key: string }) => item.key === 'allowContactInAbsence',
				).value,
				wholeDay: false,
				wholeWorkplace: createCaseTarget === CreateCaseTarget.Workplace,
			};
		} else {
			return {
				dateFrom: selectedDate,
				dateTo: selectedDate,
				allowContactInAbsence: false,
				wholeWorkplace: createCaseTarget === CreateCaseTarget.Workplace,
			};
		}
	}, [event, createCaseTarget]);

	const handleRemove = async () => {
		setIsLoading(true);
		if (eventId) {
			try {
				const response = await apiClient.delete('/events/cancel/' + eventId);
				if (response.status === 200) {
					showNotificationSuccess({
						content: 'component.calendarAbsenceEvent.deleteSuccessful',
					});
					onSuccess?.();
				} else {
					showUnexpectedError();
				}
			} catch (e) {
				displayError(e);
			}
		}
		setIsLoading(false);
	};

	const handleUpdate = async (values: CalendarAbsenceEventFormValues) => {
		setIsLoading(true);
		const { dateFrom, dateTo, workplaceMember, reason, allowContactInAbsence } =
			values;

		const timeFrom = getMomentDate(values.timeFrom);
		const timeTo = getMomentDate(values.timeTo);

		const data: {
			dateFrom: Moment;
			dateTo: Moment;
			description: string;
			allowContactInAbsence: boolean;
			eventMembers?: { workplaceMemberId: string }[];
		} = {
			dateFrom: getDateWithTime(dateFrom, timeFrom),
			dateTo: getDateWithTime(dateTo, timeTo),
			description: reason,
			allowContactInAbsence,
		};

		if (workplaceMember) {
			data.eventMembers = [
				{
					workplaceMemberId: workplaceMember.value as string,
				},
			];
		}

		if (eventId) {
			try {
				const response = await apiClient.patch('/event/' + eventId, data);
				if (response.status === 200) {
					showNotificationSuccess({
						content: 'base.absenceUpdated',
					});
					onSuccess?.();
				} else {
					showUnexpectedError();
				}
			} catch (e) {
				displayError(e);
			}
		}
		setIsLoading(false);
	};

	const handleOnCreate = () => {
		showNotificationSuccess({
			content: 'base.absenceCreated',
		});

		onSuccess?.();
	};

	if (isLoading) {
		return <Loading />;
	}

	if (createCaseTarget === CreateCaseTarget.Workplace) {
		return (
			<CalendarWorkplaceAbsenceEvent
				onRemove={handleRemove}
				onUpdate={handleUpdate}
				onCreate={handleOnCreate}
				defaultValues={defaultValues}
				workplaceId={workplaceId}
				isEdit={!!event}
			/>
		);
	}

	return (
		<CalendarWorkplaceMemberAbsenceEvent
			onUpdate={handleUpdate}
			onRemove={handleRemove}
			onCreate={handleOnCreate}
			defaultValues={defaultValues}
			isEdit={!!event}
		/>
	);
};

export default CalendarAbsenceEvent;
